import React, { useMemo, useState, useEffect, useCallback, useRef, Component, memo } from 'react';
import DOMPurify from 'dompurify';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, hexToRgba, resolveLinkProps } from '../utils';
import { getButtonIcon, resolveIconKey } from '../buttonIcons';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';
import SectionBgImage from './SectionBgImage';
import WidgetRenderer from './custom/WidgetRenderer';

// ── Error Boundary ──
class CraftErrorBoundary extends Component {
    state = { hasError: false };
    static getDerivedStateFromError() { return { hasError: true }; }
    render() { return this.state.hasError ? null : this.props.children; }
}

// ── Sanitize HTML helper ──
const sanitizeHtml = (html) => DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['b', 'i', 'u', 'em', 'strong', 's', 'a', 'br', 'span', 'sub', 'sup', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'iframe', 'div', 'img'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'width', 'height', 'frameborder', 'allow', 'allowfullscreen', 'loading', 'title', 'style', 'class'],
    ALLOW_DATA_ATTR: false,
});

// ── Keyframes (rendered inline for iframe compat) ──
const CRAFT_PUB_STYLES = `@keyframes cmarquee-pub{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}@keyframes cgradient-pub{0%{background-position:0% center}100%{background-position:200% center}}@keyframes cfloat-pulse{0%,100%{box-shadow:0 0 0 0 var(--fp-color,rgba(37,211,102,0.4))}70%{box-shadow:0 0 0 12px transparent}}`;
function CraftPubStyles() {
    return <style dangerouslySetInnerHTML={{ __html: CRAFT_PUB_STYLES }} />;
}

const MAX_RENDER_DEPTH = 30;
const PREFERS_REDUCED_MOTION = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

// ── Static class lookups ──

const COL_SPAN = {
    1: 'col-span-1', 2: 'col-span-2', 3: 'col-span-3', 4: 'col-span-4',
    5: 'col-span-5', 6: 'col-span-6', 7: 'col-span-7', 8: 'col-span-8',
    9: 'col-span-9', 10: 'col-span-10', 11: 'col-span-11', 12: 'col-span-12',
};
const MD_COL_SPAN = {
    1: 'md:col-span-1', 2: 'md:col-span-2', 3: 'md:col-span-3', 4: 'md:col-span-4',
    5: 'md:col-span-5', 6: 'md:col-span-6', 7: 'md:col-span-7', 8: 'md:col-span-8',
    9: 'md:col-span-9', 10: 'md:col-span-10', 11: 'md:col-span-11', 12: 'md:col-span-12',
};
const GAP = { none: 'gap-0', xs: 'gap-1', sm: 'gap-2', md: 'gap-4', lg: 'gap-6', xl: 'gap-8' };

// ── Craft.js Tree Renderer ──

const SIZES = { xs: '0.75rem', sm: '0.875rem', base: '1rem', lg: '1.125rem', xl: '1.25rem', '2xl': '1.5rem', '3xl': '1.875rem', '4xl': '2.25rem', '5xl': '3rem', '6xl': '3.75rem' };
const WEIGHTS = { light: 300, normal: 400, medium: 500, semibold: 600, bold: 700, extrabold: 800 };
const SHADOWS = { none: 'none', sm: '0 1px 2px rgba(0,0,0,0.05)', md: '0 4px 6px rgba(0,0,0,0.1)', lg: '0 10px 15px rgba(0,0,0,0.1)', xl: '0 20px 25px rgba(0,0,0,0.1)' };
const RADII = { none: '0', sm: '0.125rem', md: '0.375rem', lg: '0.5rem', xl: '0.75rem', '2xl': '1rem', full: '9999px' };
const SPACER_H = { sm: '16px', md: '32px', lg: '64px', xl: '96px' };
const CONTAINER_GRAV = {'top-left':{ji:'start',ai:'start'},'top-center':{ji:'center',ai:'start'},'top-right':{ji:'end',ai:'start'},'center-left':{ji:'start',ai:'center'},'center':{ji:'center',ai:'center'},'center-right':{ji:'end',ai:'center'},'bottom-left':{ji:'start',ai:'end'},'bottom-center':{ji:'center',ai:'end'},'bottom-right':{ji:'end',ai:'end'}};

const WIDGET_ICONS_MAP = {
    star: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>,
    check: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg>,
    heart: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/></svg>,
    shield: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>,
    zap: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>,
    target: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><circle cx="12" cy="12" r="10" strokeWidth={2}/><circle cx="12" cy="12" r="6" strokeWidth={2}/><circle cx="12" cy="12" r="2" strokeWidth={2}/></svg>,
    award: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"/></svg>,
    globe: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"/></svg>,
};

const SOCIAL_ICONS = {
    facebook: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>,
    instagram: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>,
    twitter: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>,
    linkedin: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>,
    youtube: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>,
    tiktok: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>,
    whatsapp: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>,
};

const LIST_BULLETS = {
    check: (c) => <svg className="w-4 h-4 shrink-0" style={{color: c}} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg>,
    arrow: (c) => <svg className="w-4 h-4 shrink-0" style={{color: c}} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6"/></svg>,
    circle: (c) => <span className="w-2 h-2 rounded-full shrink-0 mt-1.5" style={{backgroundColor: c}}/>,
    star: (c) => <svg className="w-4 h-4 shrink-0" style={{color: c}} fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>,
};

function parseVideoUrl(url) {
    if (!url) return null;
    const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (yt) return { provider: 'youtube', id: yt[1] };
    const vm = url.match(/(?:vimeo\.com\/)(\d+)/);
    if (vm) return { provider: 'vimeo', id: vm[1] };
    return null;
}

// ── Craft.js node renderers (no Craft.js dependency for public pages) ──

const CRAFT_RENDERERS = {
    CRoot: (p, children) => <>{children}</>,
    CContainer: (p, children) => {
        const mode = p.layoutMode || 'flex';
        const padT = p.paddingLinked === false ? (p.paddingTop || '0px') : (p.padding || '16px');
        const padR = p.paddingLinked === false ? (p.paddingRight || '0px') : (p.padding || '16px');
        const padB = p.paddingLinked === false ? (p.paddingBottom || '0px') : (p.padding || '16px');
        const padL = p.paddingLinked === false ? (p.paddingLeft || '0px') : (p.padding || '16px');
        const brBase = RADII[p.borderRadius] || '0';
        const brTL = p.radiusLinked === false ? (p.borderRadiusTL || '0') : brBase;
        const brTR = p.radiusLinked === false ? (p.borderRadiusTR || '0') : brBase;
        const brBR = p.radiusLinked === false ? (p.borderRadiusBR || '0') : brBase;
        const brBL = p.radiusLinked === false ? (p.borderRadiusBL || '0') : brBase;
        const w = p.widthMode === 'fixed' ? (p.widthValue || 'auto') : p.widthMode === 'hug' ? 'auto' : undefined;
        const fx = p.widthMode === 'fill' ? (p.flexGrow ?? 1) : (p.flexGrow ?? 0);
        const h = p.heightMode === 'fixed' ? (p.heightValue || 'auto') : p.heightMode === 'hug' ? 'auto' : undefined;
        let bg = p.background || 'transparent';
        if (p.bgGradient) bg = `linear-gradient(${p.bgGradientDir || '135deg'}, ${p.bgGradientFrom || '#6366f1'}, ${p.bgGradientTo || '#ec4899'})`;
        const safeBgUrl = p.bgImage && !p.bgImage.trim().toLowerCase().startsWith('javascript:') ? p.bgImage : '';
        const bgImg = safeBgUrl ? { backgroundImage: `url(${safeBgUrl})`, backgroundSize: p.bgImageSize || 'cover', backgroundPosition: p.bgImagePosition || 'center', backgroundRepeat: 'no-repeat' } : {};

        const grav = CONTAINER_GRAV[p.childGravity || 'top-left'] || {};

        // Base style
        const base = {
            padding: `${padT} ${padR} ${padB} ${padL}`,
            background: bg, borderRadius: `${brTL} ${brTR} ${brBR} ${brBL}`,
            boxShadow: SHADOWS[p.shadow] || 'none',
            borderWidth: p.borderWidth ? `${p.borderWidth}px` : undefined, borderStyle: p.borderWidth ? 'solid' : undefined, borderColor: p.borderColor || '#e5e7eb',
            overflow: p.overflow || 'visible', opacity: p.opacity != null ? p.opacity / 100 : undefined,
            position: p.position || 'relative', cursor: p.cursor || undefined,
            ...(w ? { width: w } : {}), ...(h ? { height: h } : {}),
            ...(p.widthMode === 'fill' ? { flex: `${fx} ${p.flexShrink ?? 1} 0%` } : p.widthMode === 'hug' ? { flex: '0 0 auto' } : {}),
            ...(p.minHeight ? { minHeight: p.minHeight } : {}), ...(p.maxHeight ? { maxHeight: p.maxHeight } : {}),
            ...(p.minWidth ? { minWidth: p.minWidth } : {}), ...(p.maxWidth ? { maxWidth: p.maxWidth } : {}),
            ...(p.position === 'absolute' ? { top: p.top || undefined, right: p.right || undefined, bottom: p.bottom || undefined, left: p.left || undefined, zIndex: p.zIndex || undefined } : {}),
            ...(p.backdropBlur ? { backdropFilter: `blur(${p.backdropBlur})`, WebkitBackdropFilter: `blur(${p.backdropBlur})` } : {}),
            ...bgImg,
        };

        if (mode === 'stack') {
            return <div style={{ ...base, display: 'grid', gridTemplate: '1fr / 1fr' }}>
                {children}
            </div>;
        }
        if (mode === 'free') {
            return <div style={{ ...base, display: 'block', position: 'relative' }}>{children}</div>;
        }
        // Flex (default)
        return (
            <div style={{
                ...base,
                display: 'flex', flexDirection: p.flexDirection || 'column', justifyContent: p.justifyContent, alignItems: p.alignItems,
                gap: p.gap || '12px', flexWrap: p.wrap ? 'wrap' : 'nowrap',
            }}>{children}</div>
        );
    },

    CText: (p) => {
        const Tag = p.level || 'p';
        const style = { fontSize: SIZES[p.fontSize] || SIZES.base, fontWeight: WEIGHTS[p.fontWeight] || 400, textAlign: p.textAlign || 'left' };
        if (p.letterSpacing) style.letterSpacing = p.letterSpacing;
        if (p.lineHeight) style.lineHeight = p.lineHeight;
        if (p.gradientText) {
            style.backgroundImage = `linear-gradient(135deg, ${p.gradientFrom || '#8b5cf6'}, ${p.gradientTo || '#ec4899'})`;
            style.WebkitBackgroundClip = 'text'; style.WebkitTextFillColor = 'transparent'; style.backgroundClip = 'text';
        } else { style.color = p.color || 'inherit'; }
        const hasHtml = p.text && (p.text.includes('<') || p.text.includes('&'));
        return hasHtml ? <Tag style={style} dangerouslySetInnerHTML={{ __html: sanitizeHtml(p.text) }}/> : <Tag style={style}>{p.text || ''}</Tag>;
    },

    CImage: (p) => {
        const aspect = { auto: 'auto', '1:1': '1/1', '16:9': '16/9', '4:3': '4/3', '3:2': '3/2' };
        if (!p.src) return null;
        const w = p.width && p.width !== '100%' && p.width.endsWith('px') ? `min(${p.width}, 100%)` : (p.width || '100%');
        // Generate responsive sizes hint
        const sizes = p.width && p.width.endsWith('px') ? `(max-width: 768px) 100vw, ${p.width}` : '100vw';
        const imgProps = { src: p.src, alt: p.alt || '', sizes, loading: 'lazy' };
        if (p.srcSet) imgProps.srcSet = p.srcSet;
        return (
            <div style={{ width: w, height: p.height || 'auto', borderRadius: RADII[p.borderRadius] || '0.5rem', boxShadow: SHADOWS[p.shadow] || 'none', overflow: 'hidden' }}>
                <img {...imgProps} style={{ width: '100%', height: '100%', objectFit: p.objectFit || 'cover', aspectRatio: p.height ? 'auto' : (aspect[p.aspectRatio] || 'auto') }}
                    onError={e => { e.target.style.display = 'none'; e.target.parentElement.style.background = '#f3f4f6'; e.target.parentElement.style.minHeight = '60px'; }}/>
            </div>
        );
    },

    CButton: (p) => {
        const sizeCls = p.size === 'sm' ? { px: '16px', py: '8px', fs: '0.875rem' } : p.size === 'lg' ? { px: '32px', py: '16px', fs: '1.125rem' } : { px: '24px', py: '12px', fs: '1rem' };
        const bg = p.bgColor || '#6366f1';
        const style = { display: p.fullWidth ? 'flex' : 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontWeight: 600, transition: 'all 0.3s', padding: `${sizeCls.py} ${sizeCls.px}`, fontSize: sizeCls.fs, borderRadius: RADII[p.borderRadius] || RADII.lg, cursor: 'pointer', width: p.fullWidth ? '100%' : 'auto', textDecoration: 'none' };
        switch (p.btnStyle) {
            case 'outline': Object.assign(style, { border: `2px solid ${bg}`, color: bg, background: 'transparent' }); break;
            case 'pill': Object.assign(style, { backgroundColor: bg, color: p.textColor || '#fff', borderRadius: '9999px' }); break;
            case 'ghost': Object.assign(style, { color: bg, background: 'transparent' }); break;
            default: Object.assign(style, { backgroundColor: bg, color: p.textColor || '#fff' }); break;
        }
        if (p.glow) style.boxShadow = `0 0 20px ${hexToRgba(bg, 0.4)}`;
        const iconEl = p.icon ? getButtonIcon(resolveIconKey(p.icon)) : null;
        return <a {...resolveLinkProps(p.link || '#')} role="button" style={style}>{iconEl && <span aria-hidden="true" style={{width:'16px',height:'16px'}}>{iconEl}</span>}{p.text || 'Click'}</a>;
    },

    CSpacer: (p) => <div style={{ height: SPACER_H[p.height] || SPACER_H.md }}/>,

    CDivider: (p) => {
        const w = p.width === 'sm' ? '120px' : p.width === 'md' ? '280px' : p.width === 'lg' ? '512px' : '100%';
        const c = p.color || '#e5e7eb';
        if (p.variant === 'gradient') return <div style={{ maxWidth: w, margin: '0 auto', height: '1px', background: `linear-gradient(to right, transparent, ${c}, transparent)` }}/>;
        if (p.variant === 'glow') return <div style={{ maxWidth: w, margin: '0 auto', height: '1px', backgroundColor: c, boxShadow: `0 0 8px ${hexToRgba(c, 0.5)}` }}/>;
        const bs = p.variant === 'dashed' ? 'dashed' : p.variant === 'dotted' ? 'dotted' : 'solid';
        return <hr style={{ maxWidth: w, margin: '0 auto', border: 0, borderTop: `1px ${bs} ${c}` }}/>;
    },

    CIconText: (p) => {
        const iconEl = WIDGET_ICONS_MAP[p.icon] || WIDGET_ICONS_MAP.star;
        const isTop = p.iconPosition === 'top';
        return (
            <div className={`flex ${isTop ? 'flex-col items-center text-center' : 'items-start'} gap-4`}>
                <span className="shrink-0 w-12 h-12 rounded-xl flex items-center justify-center" aria-hidden="true" style={{ backgroundColor: hexToRgba(p.iconColor || '#6366f1', 0.12), color: p.iconColor || '#6366f1' }}>
                    <span className="w-6 h-6">{iconEl}</span>
                </span>
                <div className="flex-1 min-w-0">
                    {p.title && <h4 className="font-semibold text-base" style={{ color: p.textColor || '#1f2937' }}>{p.title}</h4>}
                    {p.description && <p className="text-sm mt-1.5 leading-relaxed" style={{ color: '#6b7280' }}>{p.description}</p>}
                </div>
            </div>
        );
    },

    CList: (p) => {
        const items = typeof p.items === 'string' ? p.items.split('\n').filter(Boolean) : (p.items || []);
        return (
            <ul className="space-y-2" role="list">
                {items.map((item, i) => {
                    const text = typeof item === 'string' ? item : (item.text || '');
                    const bullet = LIST_BULLETS[p.listStyle] ? LIST_BULLETS[p.listStyle](p.iconColor || '#6366f1') : LIST_BULLETS.check(p.iconColor || '#6366f1');
                    return <li key={i} className="flex items-start gap-2.5"><span aria-hidden="true">{bullet}</span><span className="text-sm leading-relaxed" style={{ color: p.textColor || '#1f2937' }}>{text}</span></li>;
                })}
            </ul>
        );
    },

    CVideoEmbed: (p) => {
        const parsed = parseVideoUrl(p.url);
        if (!parsed) return null;
        const src = parsed.provider === 'youtube' ? `https://www.youtube-nocookie.com/embed/${parsed.id}?rel=0` : `https://player.vimeo.com/video/${parsed.id}`;
        const aspect = { '16:9': '16/9', '4:3': '4/3', '1:1': '1/1' };
        return (
            <div style={{ position: 'relative', aspectRatio: aspect[p.aspectRatio] || '16/9', borderRadius: RADII[p.borderRadius] || RADII.lg, boxShadow: SHADOWS[p.shadow] || 'none', overflow: 'hidden' }}>
                <iframe src={src} className="absolute inset-0 w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen loading="lazy" title="Video embebido" aria-label="Reproductor de video"/>
            </div>
        );
    },

    CSocialLinks: (p) => {
        const items = typeof p.items === 'string' ? (() => { try { return JSON.parse(p.items || '[]'); } catch { return []; } })() : (p.items || []);
        const sizePx = p.iconSize === 'sm' ? '32px' : p.iconSize === 'lg' ? '48px' : '40px';
        return (
            <nav className="flex flex-wrap items-center gap-3" aria-label="Redes sociales">
                {items.filter(i => i.url).map((item, idx) => {
                    const icon = SOCIAL_ICONS[item.platform];
                    if (!icon) return null;
                    return <a key={idx} {...resolveLinkProps(item.url)} aria-label={item.platform} style={{ width: sizePx, height: sizePx, borderRadius: '9999px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: hexToRgba(p.iconColor || '#6366f1', 0.1), color: p.iconColor || '#6366f1', transition: 'all 0.3s' }}>{icon}</a>;
                })}
            </nav>
        );
    },

    CStatCounter: (p) => <CraftStatCounterWidget {...p} />,

    CTestimonialCard: (p) => {
        const av = p.avatarSize === 'sm' ? 'w-8 h-8 text-xs' : p.avatarSize === 'lg' ? 'w-14 h-14 text-lg' : 'w-10 h-10 text-sm';
        const parsedVideo = p.videoUrl ? parseVideoUrl(p.videoUrl) : null;
        return (
            <figure className="rounded-xl border" role="group" style={{ backgroundColor: p.bgColor || '#ffffff', borderColor: p.borderColor || '#e5e7eb', padding: p.cardPadding || '20px' }}>
                {parsedVideo && (
                    <div className="mb-4 rounded-lg overflow-hidden" style={{ aspectRatio: '16/9' }}>
                        <iframe src={parsedVideo.provider === 'youtube' ? `https://www.youtube-nocookie.com/embed/${parsedVideo.id}?rel=0` : `https://player.vimeo.com/video/${parsedVideo.id}`}
                            className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen loading="lazy" title="Video testimonio"/>
                    </div>
                )}
                {p.rating > 0 && (
                    <div className="flex gap-0.5 mb-3" role="img" aria-label={`${p.rating} de 5 estrellas`}>
                        {[1,2,3,4,5].map(i => <svg key={i} className="w-4 h-4" aria-hidden="true" fill={i <= p.rating ? '#facc15' : 'none'} stroke={i <= p.rating ? 'none' : '#d1d5db'} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"/></svg>)}
                    </div>
                )}
                {p.quote && <blockquote className="text-sm leading-relaxed italic mb-4" style={{ color: p.textColor || '#374151' }}>"{p.quote}"</blockquote>}
                <figcaption className="flex items-center gap-3">
                    {p.avatarUrl ? <img src={p.avatarUrl} alt="" className={`${av.split(' ').slice(0,2).join(' ')} rounded-full object-cover`} loading="lazy"/> : (
                        <div className={`${av} rounded-full flex items-center justify-center font-bold`} aria-hidden="true" style={{backgroundColor: hexToRgba('#6366f1', 0.15), color: '#6366f1'}}>{(p.author || '?')[0]?.toUpperCase()}</div>
                    )}
                    <div>
                        {p.author && <p className="text-sm font-semibold" style={{ color: p.textColor || '#1f2937' }}>{p.author}</p>}
                        {p.role && <p className="text-xs" style={{ color: '#6b7280' }}>{p.role}</p>}
                    </div>
                </figcaption>
            </figure>
        );
    },

    CBadge: (p) => {
        const c = p.color || '#6366f1';
        const style = { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 12px', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 600 };
        switch (p.variant) {
            case 'outline': Object.assign(style, { border: `1.5px solid ${c}`, color: c, backgroundColor: 'transparent' }); break;
            case 'glass': Object.assign(style, { backgroundColor: hexToRgba(c, 0.15), color: c, backdropFilter: 'blur(8px)', border: `1px solid ${hexToRgba(c, 0.2)}` }); break;
            case 'glow': Object.assign(style, { backgroundColor: c, color: '#fff', boxShadow: `0 0 12px ${hexToRgba(c, 0.5)}` }); break;
            default: Object.assign(style, { backgroundColor: c, color: '#fff' }); break;
        }
        const iconEl = p.icon && WIDGET_ICONS_MAP[p.icon] ? <span className="w-3.5 h-3.5" aria-hidden="true">{WIDGET_ICONS_MAP[p.icon]}</span> : null;
        return <span role="status" style={style}>{iconEl}{p.text || 'Badge'}</span>;
    },

    CEmbed: (p) => {
        if (!p.html) return null;
        const aspect = { '16:9': '16/9', '4:3': '4/3', '1:1': '1/1', auto: 'auto' };
        const clean = DOMPurify.sanitize(p.html, {
            ALLOWED_TAGS: ['iframe', 'div', 'blockquote', 'a', 'p', 'span', 'img'],
            ALLOWED_ATTR: ['src', 'width', 'height', 'frameborder', 'allow', 'allowfullscreen', 'loading', 'title', 'style', 'class', 'href', 'target', 'rel', 'alt', 'data-instgrm-permalink', 'data-instgrm-version'],
            ALLOW_DATA_ATTR: true,
        });
        return <div style={{ aspectRatio: aspect[p.aspectRatio] || 'auto', overflow: 'hidden', borderRadius: '0.5rem' }} dangerouslySetInnerHTML={{ __html: clean }}/>;
    },

    CForm: (p, children, formCtx) => {
        const fields = typeof p.fields === 'string' ? (() => { try { return JSON.parse(p.fields || '[]'); } catch { return []; } })() : (p.fields || []);
        return <CraftFormWidget fields={fields} submitText={p.submitText} successMessage={p.successMessage} accentColor={p.accentColor} formCtx={formCtx} />;
    },

    CMarquee: (p) => {
        const isImage = p.mode === 'image';
        let items;
        if (isImage) {
            try { items = typeof p.items === 'string' ? JSON.parse(p.items || '[]') : (p.items || []); } catch { items = []; }
        } else {
            items = typeof p.items === 'string' ? p.items.split('\n').filter(Boolean) : (p.items || []);
        }
        const dur = p.speed === 'slow' ? '40s' : p.speed === 'fast' ? '15s' : '25s';
        const dir = p.direction === 'right' ? 'reverse' : 'normal';
        const track = [...items, ...items];
        if (items.length === 0) return null;
        return (
            <div className="overflow-hidden relative" role="marquee" aria-label="Contenido en movimiento">
                <div className="flex items-center gap-8 whitespace-nowrap" style={{ animation: PREFERS_REDUCED_MOTION ? 'none' : `cmarquee-pub ${dur} linear infinite`, animationDirection: dir }}>
                    {track.map((t, i) => isImage
                        ? <img key={i} src={t.src || ''} alt={t.alt || ''} style={{ height: p.imageHeight || '40px' }} className="shrink-0 px-4 object-contain" loading="lazy"/>
                        : <span key={i} className="text-sm font-medium opacity-60 shrink-0 px-4" style={{color: p.textColor || '#1f2937'}}>{typeof t === 'string' ? t : t.text}</span>
                    )}
                </div>
            </div>
        );
    },

    CAccordion: (p) => {
        const items = typeof p.items === 'string' ? (() => { try { return JSON.parse(p.items || '[]'); } catch { return []; } })() : (p.items || []);
        return (
            <div className="space-y-2" role="region" aria-label="Preguntas frecuentes">
                {items.map((item, i) => (
                    <details key={i} id={item.title ? `faq-${item.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}` : undefined} className="border rounded-lg overflow-hidden group" style={{borderColor: '#e5e7eb'}}>
                        <summary className="flex items-center justify-between px-4 py-3 cursor-pointer text-sm font-semibold list-none [&::-webkit-details-marker]:hidden" style={{color: '#1f2937'}}>
                            {item.title}
                            <svg className="w-4 h-4 shrink-0 transition-transform group-open:rotate-180" aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/></svg>
                        </summary>
                        <div className="px-4 pb-3 text-sm leading-relaxed" style={{color: '#6b7280'}}>{item.content}</div>
                    </details>
                ))}
            </div>
        );
    },

    CTabs: (p) => {
        const items = typeof p.items === 'string' ? (() => { try { return JSON.parse(p.items || '[]'); } catch { return []; } })() : (p.items || []);
        if (items.length === 0) return null;
        return <CraftTabsWidget items={items} accentColor={p.accentColor} tabVariant={p.tabVariant} />;
    },

    CCountdown: (p) => {
        if (!p.targetDate) return null;
        return <CraftCountdownWidget targetDate={p.targetDate} label={p.label} accentColor={p.accentColor} showDays={p.showDays} showHours={p.showHours} showMinutes={p.showMinutes} showSeconds={p.showSeconds} />;
    },

    CProgressBar: (p) => {
        const pct = Math.min(100, Math.max(0, Number(p.value) || 0));
        return (
            <div role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={p.label || 'Progreso'}>
                {(p.label || p.showValue !== false) && <div className="flex items-center justify-between mb-1.5"><span className="text-sm font-medium" style={{ color: '#1f2937' }}>{p.label}</span>{p.showValue !== false && <span className="text-sm font-medium" style={{ color: p.barColor || '#6366f1' }}>{pct}%</span>}</div>}
                <div className="rounded-full overflow-hidden" style={{ backgroundColor: p.bgColor || '#e5e7eb', height: p.height || '8px' }}>
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: p.barColor || '#6366f1', transition: PREFERS_REDUCED_MOTION ? 'none' : 'width 0.5s ease' }}/>
                </div>
            </div>
        );
    },

    CAlert: (p) => {
        const COLORS = { info: { bg: '#eff6ff', border: '#bfdbfe', text: '#1e40af', icon: '#3b82f6' }, success: { bg: '#f0fdf4', border: '#bbf7d0', text: '#166534', icon: '#22c55e' }, warning: { bg: '#fffbeb', border: '#fde68a', text: '#92400e', icon: '#f59e0b' }, error: { bg: '#fef2f2', border: '#fecaca', text: '#991b1b', icon: '#ef4444' } };
        const ICONS = { info: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z', success: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z', warning: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z', error: 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z' };
        const c = COLORS[p.alertType] || COLORS.info;
        const d = ICONS[p.alertType] || ICONS.info;
        return (
            <div role="alert" className="flex gap-3 p-4 rounded-lg border" style={{ backgroundColor: c.bg, borderColor: c.border }}>
                <svg className="w-5 h-5 shrink-0 mt-0.5" aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: c.icon }}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={d}/></svg>
                <div className="min-w-0">{p.title && <p className="text-sm font-semibold" style={{ color: c.text }}>{p.title}</p>}<p className="text-sm mt-0.5" style={{ color: c.text, opacity: 0.9 }}>{p.text || ''}</p></div>
            </div>
        );
    },

    CPricingCard: (p) => {
        const feats = typeof p.features === 'string' ? p.features.split('\n').filter(Boolean) : (p.features || []);
        const bg = p.bgColor || (p.highlighted ? '#6366f1' : '#ffffff');
        const txt = p.textColor || (p.highlighted ? '#ffffff' : '#1f2937');
        const sub = p.highlighted ? 'rgba(255,255,255,0.7)' : '#6b7280';
        return (
            <article className="rounded-2xl border p-6 flex flex-col" style={{ backgroundColor: bg, borderColor: p.highlighted ? 'transparent' : '#e5e7eb', boxShadow: p.highlighted ? '0 20px 40px rgba(99,102,241,0.2)' : 'none' }}>
                <h3 className="text-lg font-bold" style={{ color: txt }}>{p.name || 'Plan'}</h3>
                <div className="mt-4 mb-6"><span className="text-4xl font-extrabold" style={{ color: txt }}>{p.currency || '$'}{p.price || '0'}</span><span className="text-sm ml-1" style={{ color: sub }}>/{p.period || 'mes'}</span></div>
                <ul className="space-y-2.5 mb-6 flex-1" role="list">
                    {feats.map((f, i) => <li key={i} className="flex items-start gap-2 text-sm"><svg className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: p.highlighted ? '#a5b4fc' : '#22c55e' }}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg><span style={{ color: p.highlighted ? 'rgba(255,255,255,0.9)' : '#374151' }}>{f}</span></li>)}
                </ul>
                <a {...resolveLinkProps(p.ctaLink || '#')} role="button" className="w-full py-3 rounded-xl font-semibold text-sm text-center block" style={{ backgroundColor: p.highlighted ? '#fff' : (p.ctaColor || '#6366f1'), color: p.highlighted ? (p.ctaColor || '#6366f1') : '#fff', textDecoration: 'none' }}>{p.ctaText || 'Elegir plan'}</a>
            </article>
        );
    },

    CCarousel: (p) => {
        const items = typeof p.items === 'string' ? (() => { try { return JSON.parse(p.items || '[]'); } catch { return []; } })() : (p.items || []);
        if (items.length === 0) return null;
        return <CraftCarouselWidget items={items} autoplay={p.autoplay} interval={p.interval} showDots={p.showDots} showArrows={p.showArrows} aspectRatio={p.aspectRatio} />;
    },

    CTable: (p) => {
        const hdrs = typeof p.headers === 'string' ? p.headers.split('|').map(h => h.trim()).filter(Boolean) : (p.headers || []);
        const rows = typeof p.rows === 'string' ? p.rows.split('\n').filter(Boolean).map(r => r.split('|').map(c => c.trim())) : (p.rows || []);
        return (
            <div className="overflow-x-auto rounded-lg" style={{ border: p.bordered !== false ? '1px solid #e5e7eb' : 'none' }} role="table">
                <table className="w-full text-sm">
                    {hdrs.length > 0 && <thead><tr>{hdrs.map((h, i) => <th key={i} className="px-4 py-2.5 text-left font-semibold" scope="col" style={{ backgroundColor: p.headerBg || '#f9fafb', color: p.headerColor || '#374151', borderBottom: '1px solid #e5e7eb' }}>{h}</th>)}</tr></thead>}
                    <tbody>{rows.map((row, ri) => <tr key={ri} style={{ backgroundColor: p.striped && ri % 2 === 1 ? '#f9fafb' : 'transparent' }}>{row.map((cell, ci) => <td key={ci} className="px-4 py-2.5" style={{ color: '#4b5563', borderBottom: '1px solid #f3f4f6' }}>{cell}</td>)}</tr>)}</tbody>
                </table>
            </div>
        );
    },

    CSteps: (p) => {
        const items = typeof p.items === 'string' ? (() => { try { return JSON.parse(p.items || '[]'); } catch { return []; } })() : (p.items || []);
        const c = p.accentColor || '#6366f1';
        const conn = p.connectorColor || '#e5e7eb';
        const isVertical = p.layout === 'vertical';
        return (
            <div role="list" aria-label="Pasos" className={isVertical ? 'flex flex-col gap-0' : 'flex items-start'}>
                {items.map((step, i) => isVertical ? (
                    <div key={i} role="listitem" className="flex gap-3">
                        <div className="flex flex-col items-center">
                            <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0" style={{ backgroundColor: c, color: '#fff' }}>{i + 1}</div>
                            {i < items.length - 1 && <div className="w-0.5 flex-1 min-h-[24px]" style={{ backgroundColor: conn }}/>}
                        </div>
                        <div className="pb-6"><p className="text-sm font-semibold" style={{ color: '#1f2937' }}>{step.title || ''}</p>{step.description && <p className="text-xs mt-0.5" style={{ color: '#6b7280' }}>{step.description}</p>}</div>
                    </div>
                ) : (
                    <div key={i} role="listitem" className="flex-1 flex flex-col items-center text-center relative" style={{ minWidth: '80px' }}>
                        <div className="relative z-10 w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold" style={{ backgroundColor: c, color: '#fff' }}>{i + 1}</div>
                        {i < items.length - 1 && <div className="absolute top-5 left-[calc(50%+20px)] right-[calc(-50%+20px)] h-0.5" style={{ backgroundColor: conn }}/>}
                        <p className="text-sm font-semibold mt-2" style={{ color: '#1f2937' }}>{step.title || ''}</p>
                        {step.description && <p className="text-xs mt-0.5" style={{ color: '#6b7280' }}>{step.description}</p>}
                    </div>
                ))}
            </div>
        );
    },

    CToggle: (p) => {
        return <CraftToggleWidget labelA={p.labelA} labelB={p.labelB} contentA={p.contentA} contentB={p.contentB} accentColor={p.accentColor} />;
    },

    CNotification: (p) => {
        return <CraftNotificationWidget text={p.text} btnText={p.btnText} btnLink={p.btnLink} bgColor={p.bgColor} textColor={p.textColor} dismissible={p.dismissible} position={p.position} />;
    },

    CPopover: (p) => {
        return <CraftPopoverWidget triggerText={p.triggerText} content={p.content} triggerStyle={p.triggerStyle} accentColor={p.accentColor} width={p.width} />;
    },

    CMap: (p) => {
        const src = `https://www.google.com/maps?q=${encodeURIComponent(p.address || 'Lima, Peru')}&output=embed`;
        const br = RADII[p.borderRadius] || RADII.xl;
        return (
            <div style={{ borderRadius: br, overflow: 'hidden', height: p.height || '300px', width: '100%' }}>
                <iframe src={src} width="100%" height="100%" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" aria-label="Mapa" title="Mapa de ubicacion" allowFullScreen/>
            </div>
        );
    },

    CRating: (p) => {
        const v = parseFloat(p.value) || 0;
        const max = parseInt(p.maxStars) || 5;
        const stars = [];
        for (let i = 1; i <= max; i++) {
            if (i <= Math.floor(v)) stars.push('full');
            else if (i - v < 1 && i - v > 0) stars.push('half');
            else stars.push('empty');
        }
        const c = p.color || '#facc15';
        return (
            <div className="flex items-center gap-2" role="img" aria-label={`${v} de ${max} estrellas`}>
                <div className="flex gap-0.5">
                    {stars.map((s, i) => (
                        <svg key={i} viewBox="0 0 24 24" className="w-5 h-5" fill={s === 'empty' ? '#d1d5db' : c}>
                            {s === 'half' ? <>
                                <defs><clipPath id={`pub-half-${i}`}><rect x="0" y="0" width="12" height="24"/></clipPath></defs>
                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2z" fill="#d1d5db"/>
                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2z" fill={c} clipPath={`url(#pub-half-${i})`}/>
                            </> : <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2z"/>}
                        </svg>
                    ))}
                </div>
                <span className="text-sm font-semibold" style={{ color: p.textColor || '#1f2937' }}>{v}</span>
                {p.reviewCount && <span className="text-xs" style={{ color: p.textColor ? hexToRgba(p.textColor, 0.6) : '#6b7280' }}>{p.reviewCount}</span>}
            </div>
        );
    },

    CLogoCloud: (p) => {
        const items = typeof p.items === 'string' ? (() => { try { return JSON.parse(p.items || '[]'); } catch { return []; } })() : (p.items || []);
        const cols = parseInt(p.columns) || 4;
        if (items.length === 0) return null;
        return (
            <div className="grid" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: p.gap || '16px' }}>
                {items.map((item, i) => {
                    const img = <img src={item.src} alt={item.alt || ''} className="h-12 w-auto object-contain mx-auto transition-all duration-300" style={{ filter: p.grayscale !== false ? 'grayscale(100%)' : 'none' }} onMouseEnter={e => { if (p.grayscale !== false) e.currentTarget.style.filter = 'grayscale(0%)'; }} onMouseLeave={e => { if (p.grayscale !== false) e.currentTarget.style.filter = 'grayscale(100%)'; }} loading="lazy"/>;
                    return item.url ? <a key={i} href={item.url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center p-2">{img}</a> : <div key={i} className="flex items-center justify-center p-2">{img}</div>;
                })}
            </div>
        );
    },

    CAvatarGroup: (p) => {
        const avatars = typeof p.avatars === 'string' ? (() => { try { return JSON.parse(p.avatars || '[]'); } catch { return []; } })() : (p.avatars || []);
        const max = parseInt(p.maxShow) || 5;
        const visible = avatars.slice(0, max);
        const remaining = avatars.length - max;
        const sizes = { sm: 'w-8 h-8 text-xs', md: 'w-10 h-10 text-sm', lg: 'w-14 h-14 text-base' };
        const sizeClass = sizes[p.size] || sizes.md;
        const bg = p.bgColor || '#6366f1';
        if (avatars.length === 0) return null;
        return (
            <div className="flex items-center">
                {visible.map((av, i) => (
                    <div key={i} className={`${sizeClass} rounded-full border-2 border-white overflow-hidden flex-shrink-0`} style={{ marginLeft: i === 0 ? 0 : (p.overlap || '-8px') }} title={av.name || ''}>
                        {av.src ? <img src={av.src} alt={av.name || ''} className="w-full h-full object-cover" loading="lazy"/> : <div className="w-full h-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: bg }}>{(av.name || '?')[0].toUpperCase()}</div>}
                    </div>
                ))}
                {remaining > 0 && <div className={`${sizeClass} rounded-full border-2 border-white flex items-center justify-center text-white font-semibold flex-shrink-0`} style={{ marginLeft: p.overlap || '-8px', backgroundColor: bg }}>+{remaining}</div>}
            </div>
        );
    },

    CBeforeAfter: (p) => {
        return <CraftBeforeAfterWidget beforeSrc={p.beforeSrc} afterSrc={p.afterSrc} beforeLabel={p.beforeLabel} afterLabel={p.afterLabel} borderRadius={p.borderRadius} />;
    },

    CGallery: (p) => {
        const items = typeof p.items === 'string' ? (() => { try { return JSON.parse(p.items || '[]'); } catch { return []; } })() : (p.items || []);
        const cols = parseInt(p.columns) || 3;
        const br = RADII[p.borderRadius] || RADII.lg;
        const aspects = { '1:1': '1/1', '4:3': '4/3', '16:9': '16/9', '3:4': '3/4', auto: 'auto' };
        const ar = aspects[p.aspectRatio] || '1/1';
        if (items.length === 0) return null;
        return (
            <div className="grid" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: p.gap || '8px' }}>
                {items.map((item, i) => (
                    <div key={i} className="overflow-hidden" style={{ borderRadius: br }}>
                        <img src={item.src} alt={item.alt || ''} className="w-full h-full object-cover" style={{ aspectRatio: ar }} loading="lazy"/>
                    </div>
                ))}
            </div>
        );
    },

    CFeatureList: (p) => {
        const lines = (p.items || '').split('\n').filter(Boolean);
        return (
            <div role="list" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {lines.map((line, i) => {
                    const included = line.startsWith('+');
                    const excluded = line.startsWith('-');
                    const label = (included || excluded) ? line.slice(1).trim() : line.trim();
                    const color = included ? (p.includeColor || '#22c55e') : excluded ? (p.excludeColor || '#ef4444') : (p.textColor || '#374151');
                    return (
                        <div key={i} role="listitem" style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.875rem', color: p.textColor || '#374151' }}>
                            {included && <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                            {excluded && <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>}
                            {!included && !excluded && <span style={{ width: 18 }}/>}
                            <span>{label}</span>
                        </div>
                    );
                })}
            </div>
        );
    },

    CCoupon: (p) => {
        return <CraftCouponWidget code={p.code} description={p.description} bgColor={p.bgColor} accentColor={p.accentColor} borderStyle={p.borderStyle} />;
    },

    CCallToAction: (p) => {
        const a = p.align || 'center';
        return (
            <div style={{ padding: '32px', borderRadius: '12px', backgroundColor: p.bgColor || '#f5f3ff', textAlign: a }}>
                <h3 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '8px', color: p.textColor || '#1f2937' }}>{p.title || 'Empieza hoy'}</h3>
                {p.subtitle && <p style={{ fontSize: '1rem', marginBottom: '20px', color: p.textColor ? p.textColor + 'bb' : '#6b7280' }}>{p.subtitle}</p>}
                <a {...resolveLinkProps(p.btnLink || '#')} style={{ display: 'inline-block', padding: '12px 24px', borderRadius: '8px', fontWeight: 600, color: '#fff', fontSize: '0.875rem', backgroundColor: p.btnColor || '#6366f1', textDecoration: 'none', transition: 'opacity 0.2s' }}>{p.btnText || 'Comenzar gratis'}</a>
            </div>
        );
    },

    CTimeline: (p) => {
        let parsed = [];
        try { parsed = typeof p.items === 'string' ? JSON.parse(p.items) : (p.items || []); } catch { parsed = []; }
        if (!parsed.length) return null;
        const accent = p.accentColor || '#6366f1';
        const line = p.lineColor || '#e5e7eb';
        return (
            <div style={{ position: 'relative', paddingLeft: '32px' }}>
                <div style={{ position: 'absolute', left: '14px', top: 0, bottom: 0, width: '2px', backgroundColor: line }}/>
                {parsed.map((item, i) => (
                    <div key={i} style={{ position: 'relative', marginBottom: i < parsed.length - 1 ? '24px' : 0 }}>
                        <div style={{ position: 'absolute', left: '-32px', width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700, color: '#fff', backgroundColor: accent }}>{i + 1}</div>
                        <h4 style={{ fontWeight: 600, fontSize: '0.875rem', color: '#1f2937' }}>{item.title}</h4>
                        {item.description && <p style={{ fontSize: '0.875rem', marginTop: '2px', color: '#6b7280' }}>{item.description}</p>}
                    </div>
                ))}
            </div>
        );
    },

    CQuote: (p) => {
        const sz = { sm: '0.875rem', base: '1rem', lg: '1.125rem', xl: '1.25rem', '2xl': '1.5rem', '3xl': '1.875rem' }[p.fontSize || 'xl'] || '1.25rem';
        const a = p.align || 'left';
        const accent = p.accentColor || '#6366f1';
        return (
            <figure style={{ textAlign: a }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill={accent} opacity="0.2" style={{ marginBottom: 8, ...(a === 'center' ? { margin: '0 auto 8px' } : a === 'right' ? { marginLeft: 'auto', marginBottom: 8 } : {}) }}><path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z"/><path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z"/></svg>
                <blockquote style={{ fontStyle: 'italic', fontSize: sz, lineHeight: 1.6, color: '#374151' }}>{p.text || 'Quote text'}</blockquote>
                {(p.author || p.role) && (
                    <figcaption style={{ marginTop: '12px' }}>
                        {p.author && <span style={{ fontWeight: 600, fontSize: '0.875rem', color: '#1f2937' }}>{p.author}</span>}
                        {p.author && p.role && <span style={{ margin: '0 6px', color: '#d1d5db' }}>&mdash;</span>}
                        {p.role && <span style={{ fontSize: '0.875rem', color: accent }}>{p.role}</span>}
                    </figcaption>
                )}
            </figure>
        );
    },

    CComparisonTable: (p) => {
        const hdrs = typeof p.headers === 'string' ? (() => { try { return JSON.parse(p.headers); } catch { return []; } })() : (p.headers || []);
        const feats = typeof p.features === 'string' ? (() => { try { return JSON.parse(p.features); } catch { return []; } })() : (p.features || []);
        const c = p.accentColor || '#6366f1';
        const hi = Number(p.highlightCol) || 0;
        return (
            <div className="overflow-x-auto rounded-xl border" style={{ borderColor: '#e5e7eb' }}>
                <table className="w-full text-sm">
                    <thead><tr><th className="px-4 py-3 text-left font-semibold" style={{ color: '#374151', backgroundColor: '#f9fafb' }}>Caracteristica</th>
                        {hdrs.map((h, i) => <th key={i} className="px-4 py-3 text-center font-semibold" style={{ color: i === hi ? '#fff' : '#374151', backgroundColor: i === hi ? c : '#f9fafb' }}>{h}</th>)}
                    </tr></thead>
                    <tbody>{feats.map((f, ri) => (
                        <tr key={ri} style={{ borderTop: '1px solid #f3f4f6' }}>
                            <td className="px-4 py-2.5 font-medium" style={{ color: '#374151' }}>{f.name}</td>
                            {(f.cols || []).map((v, ci) => <td key={ci} className="px-4 py-2.5 text-center" style={{ backgroundColor: ci === hi ? hexToRgba(c, 0.05) : 'transparent' }}>
                                {v ? <svg className="w-5 h-5 mx-auto" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                                    : <svg className="w-5 h-5 mx-auto" fill="none" viewBox="0 0 24 24" stroke="#d1d5db" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>}
                            </td>)}
                        </tr>
                    ))}</tbody>
                </table>
            </div>
        );
    },

    CVideoTestimonial: (p) => {
        const parsed = parseVideoUrl(p.videoUrl);
        const c = p.accentColor || '#6366f1';
        return (
            <div className="rounded-xl overflow-hidden border" style={{ borderColor: '#e5e7eb' }}>
                {parsed && <div style={{ aspectRatio: '16/9' }}><iframe src={parsed.provider === 'youtube' ? `https://www.youtube-nocookie.com/embed/${parsed.id}?rel=0` : `https://player.vimeo.com/video/${parsed.id}`} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen loading="lazy" title="Video testimonio"/></div>}
                <div className="p-5">
                    {p.quote && <p className="text-sm leading-relaxed italic mb-3" style={{ color: '#374151' }}>"{p.quote}"</p>}
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold" style={{ backgroundColor: hexToRgba(c, 0.15), color: c }}>{(p.author || '?')[0]?.toUpperCase()}</div>
                        <div>{p.author && <p className="text-sm font-semibold" style={{ color: '#1f2937' }}>{p.author}</p>}{p.role && <p className="text-xs" style={{ color: '#6b7280' }}>{p.role}</p>}</div>
                    </div>
                </div>
            </div>
        );
    },

    CROICalculator: (p) => <CraftROICalculatorWidget {...p} />,

    CBlogCard: (p) => {
        const br = RADII[p.borderRadius] || RADII.xl;
        return (
            <article className="overflow-hidden border" style={{ borderRadius: br, borderColor: '#e5e7eb' }}>
                {p.image && <img src={p.image} alt={p.title || ''} className="w-full object-cover" style={{ height: '200px' }} loading="lazy"/>}
                <div className="p-5">
                    {p.tag && <span className="inline-block text-xs font-semibold px-2.5 py-0.5 rounded-full mb-3" style={{ backgroundColor: hexToRgba(p.tagColor || '#6366f1', 0.1), color: p.tagColor || '#6366f1' }}>{p.tag}</span>}
                    {p.title && <h3 className="text-lg font-bold mb-2" style={{ color: '#1f2937' }}>{p.title}</h3>}
                    {p.excerpt && <p className="text-sm leading-relaxed mb-4" style={{ color: '#6b7280' }}>{p.excerpt}</p>}
                    <a {...resolveLinkProps(p.link || '#')} className="text-sm font-semibold inline-flex items-center gap-1" style={{ color: p.tagColor || '#6366f1', textDecoration: 'none' }}>{p.linkText || 'Leer mas'} <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6"/></svg></a>
                </div>
            </article>
        );
    },

    CFloatingButton: (p) => <CraftFloatingButtonWidget {...p} />,

    CPricingToggle: (p) => <CraftPricingToggleWidget {...p} />,

    CImageShowcase: (p) => {
        const feats = typeof p.features === 'string' ? p.features.split('\n').filter(Boolean) : (p.features || []);
        const isRight = p.imagePosition === 'right';
        const c = p.accentColor || '#6366f1';
        return (
            <div className={`flex ${isRight ? 'flex-row-reverse' : 'flex-row'} gap-8 items-center`} style={{ flexWrap: 'wrap' }}>
                <div className="flex-1 min-w-[200px]">{p.image && <img src={p.image} alt={p.title || ''} className="rounded-xl w-full" style={{ maxHeight: '400px', objectFit: 'cover' }} loading="lazy"/>}</div>
                <div className="flex-1 min-w-[200px]">
                    {p.title && <h3 className="text-xl font-bold mb-2" style={{ color: '#1f2937' }}>{p.title}</h3>}
                    {p.description && <p className="text-sm mb-4 leading-relaxed" style={{ color: '#6b7280' }}>{p.description}</p>}
                    {feats.length > 0 && <ul className="space-y-2">{feats.map((f, i) => <li key={i} className="flex items-start gap-2 text-sm"><svg className="w-4 h-4 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg><span style={{ color: '#374151' }}>{f}</span></li>)}</ul>}
                </div>
            </div>
        );
    },

    CGradientText: (p) => {
        const parsedColors = typeof p.colors === 'string' ? p.colors.split(',').map(c => c.trim()) : (p.colors || ['#6366f1', '#ec4899', '#f59e0b']);
        const gradient = parsedColors.join(', ');
        return (
            <span style={{ fontSize: SIZES[p.fontSize] || SIZES['4xl'], fontWeight: WEIGHTS[p.fontWeight] || 700, backgroundImage: `linear-gradient(90deg, ${gradient}, ${parsedColors[0]})`, backgroundSize: '200% auto', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', animation: PREFERS_REDUCED_MOTION ? 'none' : `cgradient-pub ${p.speed === 'slow' ? '6s' : p.speed === 'fast' ? '2s' : '4s'} linear infinite`, display: 'inline-block' }}>
                {p.text || 'Texto Gradiente'}
            </span>
        );
    },

    CLogoMarquee: (p) => {
        const items = typeof p.items === 'string' ? (() => { try { return JSON.parse(p.items || '[]'); } catch { return []; } })() : (p.items || []);
        const dur = p.speed === 'slow' ? '40s' : p.speed === 'fast' ? '15s' : '25s';
        const dir = p.direction === 'right' ? 'reverse' : 'normal';
        const track = [...items, ...items];
        if (items.length === 0) return null;
        return (
            <div className="overflow-hidden">
                <div className="flex items-center whitespace-nowrap" style={{ animation: PREFERS_REDUCED_MOTION ? 'none' : `cmarquee-pub ${dur} linear infinite`, animationDirection: dir, gap: p.gap || '40px' }}>
                    {track.map((item, i) => <img key={i} src={item.src} alt={item.alt || ''} className="shrink-0 object-contain transition-all duration-300" style={{ height: p.logoHeight || '40px', filter: p.grayscale !== false ? 'grayscale(100%)' : 'none' }} onMouseEnter={e => { if (p.grayscale !== false) e.currentTarget.style.filter = 'grayscale(0%)'; }} onMouseLeave={e => { if (p.grayscale !== false) e.currentTarget.style.filter = 'grayscale(100%)'; }} loading="lazy"/>)}
                </div>
            </div>
        );
    },

    CScrollProgress: (p) => <CraftScrollProgressWidget barColor={p.barColor} height={p.height} position={p.position} />,

    CBackToTop: (p) => <CraftBackToTopWidget bgColor={p.bgColor} iconColor={p.iconColor} size={p.size} borderRadius={p.borderRadius} />,

    CTrustBar: (p) => {
        const logos = typeof p.items === 'string' ? (() => { try { return JSON.parse(p.items || '[]'); } catch { return []; } })() : (p.items || []);
        return (
            <div className="py-4 px-6 rounded-xl text-center" style={{ backgroundColor: p.bgColor || '#f9fafb' }}>
                {p.label && <p className="text-xs font-medium uppercase tracking-wider mb-4" style={{ color: '#9ca3af' }}>{p.label}</p>}
                <div className="flex items-center justify-center flex-wrap gap-8">
                    {logos.filter(l => l.src).map((l, i) => <img key={i} src={l.src} alt={l.alt || ''} className="object-contain" style={{ height: p.logoHeight || '24px', filter: p.grayscale !== false ? 'grayscale(100%) opacity(0.5)' : 'none' }} loading="lazy"/>)}
                </div>
            </div>
        );
    },

    CMetricCard: (p) => {
        const c = p.accentColor || '#6366f1';
        const iconEl = WIDGET_ICONS_MAP[p.icon] || WIDGET_ICONS_MAP.zap;
        const isUp = p.trend !== 'down';
        return (
            <div className="p-5 rounded-xl border" style={{ backgroundColor: p.bgColor || '#ffffff', borderColor: '#e5e7eb' }}>
                <div className="flex items-start justify-between mb-3">
                    <span className="w-10 h-10 rounded-lg flex items-center justify-center" aria-hidden="true" style={{ backgroundColor: hexToRgba(c, 0.1), color: c }}><span className="w-5 h-5">{iconEl}</span></span>
                    {p.trendValue && <span className="text-xs font-semibold px-2 py-0.5 rounded-full flex items-center gap-0.5" style={{ backgroundColor: isUp ? '#f0fdf4' : '#fef2f2', color: isUp ? '#16a34a' : '#dc2626' }}>
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d={isUp ? 'M5 10l7-7m0 0l7 7' : 'M19 14l-7 7m0 0l-7-7'}/></svg>
                        {p.trendValue}
                    </span>}
                </div>
                <div className="text-2xl font-extrabold" style={{ color: '#1f2937' }}>{p.value || '0'}</div>
                {p.label && <p className="text-sm mt-1" style={{ color: '#6b7280' }}>{p.label}</p>}
            </div>
        );
    },

    CVideoPlayer: (p) => {
        const br = RADII[p.borderRadius] || RADII.xl;
        if (!p.src && !p.poster) return null;
        return (
            <div className="overflow-hidden" style={{ borderRadius: br, boxShadow: SHADOWS[p.shadow] || 'none' }}>
                {p.src ? <video src={p.src} poster={p.poster || ''} controls={!p.autoplay} autoPlay={p.autoplay} loop={p.loop} muted={p.muted || p.autoplay} playsInline className="w-full" style={{ display: 'block' }}/> : p.poster ? <img src={p.poster} alt="" className="w-full" style={{ display: 'block' }} loading="lazy"/> : null}
            </div>
        );
    },

    CIntegrationGrid: (p) => {
        const logos = typeof p.items === 'string' ? (() => { try { return JSON.parse(p.items || '[]'); } catch { return []; } })() : (p.items || []);
        const cols = parseInt(p.columns) || 4;
        if (logos.length === 0) return null;
        return (
            <div className="grid" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: p.gap || '16px' }}>
                {logos.map((l, i) => (
                    <div key={i} className="flex flex-col items-center justify-center p-4 rounded-xl border transition-all hover:shadow-md" style={{ backgroundColor: p.bgColor || '#ffffff', borderColor: '#e5e7eb' }}>
                        {l.src && <img src={l.src} alt={l.alt || ''} className="object-contain" style={{ height: p.logoHeight || '32px' }} loading="lazy"/>}
                        {p.showLabels !== false && l.name && <span className="text-[10px] font-medium mt-2 text-center" style={{ color: '#6b7280' }}>{l.name}</span>}
                    </div>
                ))}
            </div>
        );
    },

    CTestimonialWall: (p) => {
        const items = typeof p.items === 'string' ? (() => { try { return JSON.parse(p.items || '[]'); } catch { return []; } })() : (p.items || []);
        const c = p.accentColor || '#6366f1';
        const cols = parseInt(p.columns) || 3;
        if (items.length === 0) return null;
        return (
            <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
                {items.map((t, i) => (
                    <div key={i} className="p-4 rounded-xl border" style={{ borderColor: '#e5e7eb', backgroundColor: '#ffffff' }}>
                        {t.rating > 0 && <div className="flex gap-0.5 mb-2">{[1,2,3,4,5].map(s => <svg key={s} className="w-3 h-3" fill={s <= t.rating ? '#facc15' : '#e5e7eb'} viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>)}</div>}
                        <p className="text-sm leading-relaxed mb-3" style={{ color: '#374151' }}>"{t.quote}"</p>
                        <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold" style={{ backgroundColor: hexToRgba(c, 0.15), color: c }}>{(t.author || '?')[0]?.toUpperCase()}</div>
                            <div><p className="text-xs font-semibold" style={{ color: '#1f2937' }}>{t.author}</p>{t.role && <p className="text-[10px]" style={{ color: '#9ca3af' }}>{t.role}</p>}</div>
                        </div>
                    </div>
                ))}
            </div>
        );
    },

    CSplitContent: (p) => {
        const isRight = p.imagePosition === 'right';
        const c = p.ctaColor || '#6366f1';
        return (
            <div className={`flex ${isRight ? 'flex-row-reverse' : 'flex-row'} gap-10 items-center`} style={{ flexWrap: 'wrap' }}>
                <div className="flex-1 min-w-[250px]">{p.image && <img src={p.image} alt={p.title || ''} className="rounded-2xl w-full shadow-lg" style={{ maxHeight: '440px', objectFit: 'cover' }} loading="lazy"/>}</div>
                <div className="flex-1 min-w-[250px]">
                    {p.tag && <span className="inline-block text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full mb-4" style={{ backgroundColor: hexToRgba(c, 0.1), color: c }}>{p.tag}</span>}
                    {p.title && <h2 className="text-2xl font-extrabold mb-3" style={{ color: '#1f2937' }}>{p.title}</h2>}
                    {p.subtitle && <p className="text-lg font-medium mb-2" style={{ color: '#4b5563' }}>{p.subtitle}</p>}
                    {p.description && <p className="text-sm leading-relaxed mb-6" style={{ color: '#6b7280' }}>{p.description}</p>}
                    {p.ctaText && <a {...resolveLinkProps(p.ctaLink || '#')} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm text-white" style={{ backgroundColor: c, textDecoration: 'none' }}>{p.ctaText}<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6"/></svg></a>}
                </div>
            </div>
        );
    },

    CCodeBlock: (p) => {
        const lines = (p.code || '').split('\n');
        return <CraftCodeBlockWidget code={p.code} language={p.language} showLineNumbers={p.showLineNumbers} bgColor={p.bgColor} textColor={p.textColor} />;
    },

    CChecklistCard: (p) => {
        const checks = typeof p.items === 'string' ? p.items.split('\n').filter(Boolean) : (p.items || []);
        const c = p.ctaColor || '#6366f1';
        return (
            <div className="p-6 rounded-2xl border" style={{ backgroundColor: p.bgColor || '#ffffff', borderColor: '#e5e7eb' }}>
                {p.title && <h3 className="text-lg font-bold mb-4" style={{ color: '#1f2937' }}>{p.title}</h3>}
                <ul className="space-y-3 mb-6" role="list">
                    {checks.map((item, i) => <li key={i} className="flex items-start gap-3"><span className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ backgroundColor: hexToRgba(p.iconColor || '#22c55e', 0.15) }}><svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke={p.iconColor || '#22c55e'} strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg></span><span className="text-sm" style={{ color: '#374151' }}>{typeof item === 'string' ? item : item.text}</span></li>)}
                </ul>
                {p.ctaText && <a {...resolveLinkProps(p.ctaLink || '#')} className="w-full py-3 rounded-xl font-semibold text-sm text-center block text-white" style={{ backgroundColor: c, textDecoration: 'none' }}>{p.ctaText}</a>}
            </div>
        );
    },
};

// ── Interactive widgets (stateful, need hooks) ──

function CraftCouponWidget({ code, description, bgColor, accentColor, borderStyle }) {
    const [copied, setCopied] = useState(false);
    const c = accentColor || '#6366f1';
    const handleCopy = () => {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(code || 'SAVE20').then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            });
        }
    };
    return (
        <div style={{ padding: '20px', borderRadius: '8px', textAlign: 'center', backgroundColor: bgColor || '#eff6ff', border: `2px ${borderStyle || 'dashed'} ${c}` }}>
            <p style={{ fontSize: '0.875rem', marginBottom: '8px', color: '#6b7280' }}>{description || '20% de descuento'}</p>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '12px', padding: '10px 20px', borderRadius: '6px', backgroundColor: '#fff', border: `1px solid ${c}22` }}>
                <span style={{ fontFamily: 'monospace', fontSize: '1.125rem', fontWeight: 700, letterSpacing: '0.05em', color: c }}>{code || 'SAVE20'}</span>
                <button onClick={handleCopy} style={{ fontSize: '0.75rem', padding: '4px 8px', borderRadius: '4px', backgroundColor: c, color: '#fff', border: 'none', cursor: 'pointer' }}>{copied ? 'Copiado!' : 'Copiar'}</button>
            </div>
        </div>
    );
}

function CraftToggleWidget({ labelA, labelB, contentA, contentB, accentColor }) {
    const [active, setActive] = useState(false);
    const c = accentColor || '#6366f1';
    return (
        <div className="flex flex-col items-center gap-4">
            <div className="flex items-center gap-3">
                <span className="text-sm font-medium" style={{ color: !active ? c : '#6b7280' }}>{labelA || 'Mensual'}</span>
                <button onClick={() => setActive(!active)} className="relative w-12 h-6 rounded-full transition-colors" style={{ backgroundColor: active ? c : '#d1d5db' }} role="switch" aria-checked={active} aria-label="Alternar contenido">
                    <span className="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform" style={{ left: active ? '26px' : '2px' }}/>
                </button>
                <span className="text-sm font-medium" style={{ color: active ? c : '#6b7280' }}>{labelB || 'Anual'}</span>
            </div>
            <div className="text-2xl font-bold" style={{ color: '#1f2937' }}>{active ? (contentB || '$290/ano') : (contentA || '$29/mes')}</div>
        </div>
    );
}

function CraftNotificationWidget({ text, btnText, btnLink, bgColor, textColor, dismissible, position }) {
    const [dismissed, setDismissed] = useState(false);
    if (dismissed) return null;
    const bg = bgColor || '#6366f1';
    const txt = textColor || '#ffffff';
    const isSticky = position === 'sticky-top' || position === 'sticky-bottom';
    const stickyStyle = isSticky ? { position: 'fixed', left: 0, right: 0, zIndex: 9999, borderRadius: 0, ...(position === 'sticky-top' ? { top: 0 } : { bottom: 0 }) } : {};
    return (
        <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg" style={{ backgroundColor: bg, color: txt, ...stickyStyle }} role="alert">
            <span className="text-sm font-medium flex-1">{text || 'Oferta especial'}</span>
            <div className="flex items-center gap-2 shrink-0">
                {btnText && <a {...resolveLinkProps(btnLink || '#')} className="text-sm font-semibold underline" style={{ color: txt }}>{btnText}</a>}
                {dismissible !== false && <button onClick={() => setDismissed(true)} className="opacity-70 hover:opacity-100" aria-label="Cerrar"><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg></button>}
            </div>
        </div>
    );
}

function CraftPopoverWidget({ triggerText, content, triggerStyle, accentColor, width }) {
    const [open, setOpen] = useState(false);
    const c = accentColor || '#6366f1';
    const triggerEl = triggerStyle === 'button'
        ? <button onClick={() => setOpen(!open)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-white" style={{ backgroundColor: c }}>{triggerText || 'Mas info'}</button>
        : triggerStyle === 'icon'
            ? <button onClick={() => setOpen(!open)} className="inline-flex items-center justify-center w-6 h-6 rounded-full text-white" style={{ backgroundColor: c }} aria-label={triggerText || 'Mas info'}><svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg></button>
            : <button onClick={() => setOpen(!open)} className="text-sm underline cursor-pointer" style={{ color: c, background: 'none', border: 'none', padding: 0 }}>{triggerText || 'Mas info'}</button>;
    return (
        <div className="inline-block relative">
            {triggerEl}
            {open && <div className="absolute left-0 top-full mt-2 p-3 rounded-lg border shadow-lg text-sm z-50" style={{ backgroundColor: '#fff', borderColor: '#e5e7eb', color: '#374151', width: width || '280px', maxWidth: '90vw' }}>
                {content || 'Descripcion detallada...'}
            </div>}
        </div>
    );
}

function CraftTabsWidget({ items, accentColor, tabVariant }) {
    const [active, setActive] = useState(0);
    const c = accentColor || '#6366f1';
    const variant = tabVariant || 'underline';

    const tabStyle = (isActive) => {
        if (variant === 'pills') return { padding: '6px 16px', borderRadius: '9999px', fontSize: '0.875rem', fontWeight: 500, backgroundColor: isActive ? c : 'transparent', color: isActive ? '#fff' : '#6b7280', transition: 'all 0.2s' };
        if (variant === 'cards') return { padding: '8px 16px', borderRadius: '0.5rem 0.5rem 0 0', fontSize: '0.875rem', fontWeight: 500, backgroundColor: isActive ? '#fff' : 'transparent', color: isActive ? c : '#6b7280', border: isActive ? '1px solid #e5e7eb' : '1px solid transparent', borderBottom: isActive ? '1px solid #fff' : '1px solid transparent', marginBottom: '-1px', transition: 'all 0.2s' };
        return { padding: '8px 16px', fontSize: '0.875rem', fontWeight: 500, color: isActive ? c : '#6b7280', position: 'relative' };
    };

    return (
        <div>
            <div role="tablist" className={`flex ${variant === 'pills' ? 'gap-1 p-1' : variant === 'cards' ? '' : 'border-b'}`}
                style={variant === 'pills' ? { backgroundColor: hexToRgba(c, 0.08), borderRadius: '9999px', display: 'inline-flex' } : variant === 'cards' ? { borderBottom: '1px solid #e5e7eb' } : { borderColor: '#e5e7eb' }}>
                {items.map((item, i) => (
                    <button key={i} role="tab" id={`ctab-${i}`} aria-selected={active === i} aria-controls={`ctabpanel-${i}`} onClick={() => setActive(i)} style={tabStyle(active === i)}>
                        {item.title}
                        {variant === 'underline' && active === i && <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: c }} />}
                    </button>
                ))}
            </div>
            <div role="tabpanel" id={`ctabpanel-${active}`} aria-labelledby={`ctab-${active}`} className="pt-4 text-sm leading-relaxed" style={{ color: '#1f2937' }}>
                {items[active]?.content || ''}
                {items[active]?.image && <img src={items[active].image} alt="" className="mt-3 rounded-lg max-w-full" style={{ maxHeight: '400px', objectFit: 'cover' }} loading="lazy"/>}
            </div>
        </div>
    );
}

function CraftCountdownWidget({ targetDate, label, accentColor, showDays, showHours, showMinutes, showSeconds }) {
    const [tl, setTl] = useState({ d: 0, h: 0, m: 0, s: 0 });
    useEffect(() => {
        const target = new Date(targetDate).getTime();
        const tick = () => {
            const diff = Math.max(0, target - Date.now());
            setTl({ d: Math.floor(diff / 86400000), h: Math.floor((diff % 86400000) / 3600000), m: Math.floor((diff % 3600000) / 60000), s: Math.floor((diff % 60000) / 1000) });
        };
        tick();
        const iv = setInterval(tick, PREFERS_REDUCED_MOTION ? 60000 : 1000);
        return () => clearInterval(iv);
    }, [targetDate]);
    const c = accentColor || '#6366f1';
    const units = [
        showDays !== false && { v: tl.d, l: 'Dias' },
        showHours !== false && { v: tl.h, l: 'Horas' },
        showMinutes !== false && { v: tl.m, l: 'Min' },
        showSeconds !== false && { v: tl.s, l: 'Seg' },
    ].filter(Boolean);
    return (
        <div>
            {label && <p className="text-sm font-medium mb-4 text-center" style={{ color: '#6b7280' }}>{label}</p>}
            <div className="flex items-center justify-center gap-3">
                {units.map((u, i) => (
                    <div key={i} className="flex flex-col items-center">
                        <div className="w-16 h-16 rounded-xl flex items-center justify-center text-2xl font-bold" style={{ backgroundColor: hexToRgba(c, 0.1), color: c }}>
                            {String(u.v).padStart(2, '0')}
                        </div>
                        <span className="text-[10px] font-medium mt-1.5 uppercase tracking-wider" style={{ color: '#6b7280' }}>{u.l}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function CraftFormWidget({ fields, submitText, successMessage, accentColor, formCtx }) {
    const [formData, setFormData] = useState({});
    const [status, setStatus] = useState('idle'); // idle | sending | success | error
    const [honey, setHoney] = useState('');

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (honey) return; // honeypot
        if (status === 'sending') return;
        setStatus('sending');
        try {
            const payload = {
                block_type: formCtx?.blockType || 'custom',
                block_id: formCtx?.blockId || null,
                source: 'craft_form',
                name: formData.name || formData.nombre || '',
                email: formData.email || '',
                phone: formData.phone || formData.tel || formData.telefono || '',
                data: formData,
            };
            const csrfMeta = document.querySelector('meta[name="csrf-token"]');
            const res = await fetch('/api/leads', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', ...(csrfMeta ? { 'X-CSRF-TOKEN': csrfMeta.content } : {}) },
                body: JSON.stringify(payload),
            });
            if (res.ok) { setStatus('success'); setFormData({}); }
            else setStatus('error');
        } catch { setStatus('error'); }
    }, [formData, honey, status, formCtx]);

    const c = accentColor || '#6366f1';
    if (status === 'success') return (
        <div className="text-center py-6">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center" style={{ backgroundColor: hexToRgba(c, 0.1) }}>
                <svg className="w-6 h-6" style={{ color: c }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            </div>
            <p className="text-sm font-medium" style={{ color: '#1f2937' }}>{successMessage || 'Gracias!'}</p>
        </div>
    );

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="hidden"><input tabIndex={-1} autoComplete="off" value={honey} onChange={e => setHoney(e.target.value)} /></div>
            {fields.map((f, i) => (
                <div key={i}>
                    <label htmlFor={`cf-${f.name || i}`} className="block text-sm font-medium mb-1.5 text-gray-700">
                        {f.label}{f.required && <span style={{ color: c }} aria-hidden="true"> *</span>}
                    </label>
                    {f.type === 'textarea' ? (
                        <textarea id={`cf-${f.name || i}`} placeholder={f.placeholder || ''} rows={3} required={f.required}
                            aria-required={f.required || undefined}
                            value={formData[f.name || f.label] || ''} onChange={e => setFormData(prev => ({ ...prev, [f.name || f.label]: e.target.value }))}
                            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm bg-white" />
                    ) : (
                        <input id={`cf-${f.name || i}`} type={f.type || 'text'} placeholder={f.placeholder || ''} required={f.required}
                            aria-required={f.required || undefined}
                            value={formData[f.name || f.label] || ''} onChange={e => setFormData(prev => ({ ...prev, [f.name || f.label]: e.target.value }))}
                            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm bg-white" />
                    )}
                </div>
            ))}
            <button type="submit" disabled={status === 'sending'}
                className="w-full px-6 py-3 rounded-lg font-semibold text-white transition-opacity disabled:opacity-60"
                style={{ backgroundColor: c }}>
                {status === 'sending' ? 'Enviando...' : (submitText || 'Enviar')}
            </button>
            {status === 'error' && <p className="text-sm text-red-500 text-center">Error al enviar. Intenta de nuevo.</p>}
        </form>
    );
}

function CraftCarouselWidget({ items, autoplay, interval, showDots, showArrows, aspectRatio }) {
    const [current, setCurrent] = useState(0);
    const aspect = { '16:9': '16/9', '4:3': '4/3', '1:1': '1/1', '21:9': '21/9' };
    useEffect(() => {
        if (!autoplay || items.length <= 1 || PREFERS_REDUCED_MOTION) return;
        const iv = setInterval(() => setCurrent(c => (c + 1) % items.length), (interval || 5) * 1000);
        return () => clearInterval(iv);
    }, [autoplay, interval, items.length]);
    const prev = () => setCurrent((current - 1 + items.length) % items.length);
    const next = () => setCurrent((current + 1) % items.length);
    return (
        <div className="relative rounded-xl overflow-hidden group" role="region" aria-roledescription="carousel" aria-label="Carrusel de imagenes" style={{ aspectRatio: aspect[aspectRatio] || '16/9' }}>
            {items.map((item, i) => <img key={i} src={item.src} alt={item.alt || ''} className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500" aria-hidden={i !== current} style={{ opacity: i === current ? 1 : 0 }} loading="lazy" onError={e => { e.target.style.display = 'none'; }}/>)}
            {showArrows !== false && items.length > 1 && <>
                <button onClick={prev} aria-label="Slide anterior" className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg></button>
                <button onClick={next} aria-label="Slide siguiente" className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg></button>
            </>}
            {showDots !== false && items.length > 1 && <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5" role="tablist">{items.map((_, i) => <button key={i} role="tab" aria-selected={i === current} aria-label={`Slide ${i + 1}`} onClick={() => setCurrent(i)} className={`w-2 h-2 rounded-full transition-all ${i === current ? 'bg-white scale-125' : 'bg-white/50'}`}/>)}</div>}
        </div>
    );
}

// ── Stat Counter with count-up animation ──
function CraftStatCounterWidget({ value, prefix, suffix, label, valueColor, labelColor, valueSize, animated, duration }) {
    const numericVal = parseFloat(String(value || '0').replace(/[^0-9.-]/g, ''));
    const displayStr = String(value || '0');
    const isNumeric = !isNaN(numericVal) && isFinite(numericVal);
    const [displayVal, setDisplayVal] = useState(animated !== false && isNumeric && !PREFERS_REDUCED_MOTION ? 0 : numericVal);
    const [hasAnimated, setHasAnimated] = useState(false);
    const elRef = useRef(null);
    useEffect(() => {
        if (animated === false || !isNumeric || PREFERS_REDUCED_MOTION || hasAnimated) return;
        const observer = new IntersectionObserver(([entry]) => {
            if (!entry.isIntersecting) return;
            observer.disconnect();
            setHasAnimated(true);
            const dur = duration || 2000;
            const start = performance.now();
            const step = (now) => {
                const elapsed = now - start;
                const progress = Math.min(elapsed / dur, 1);
                const eased = 1 - Math.pow(1 - progress, 3);
                setDisplayVal(Math.round(numericVal * eased * 100) / 100);
                if (progress < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
        }, { threshold: 0.3 });
        if (elRef.current) observer.observe(elRef.current);
        return () => observer.disconnect();
    }, [animated, isNumeric, numericVal, duration, hasAnimated]);

    const shown = isNumeric && animated !== false && !PREFERS_REDUCED_MOTION
        ? (Number.isInteger(numericVal) ? Math.round(displayVal) : displayVal.toFixed(String(numericVal).split('.')[1]?.length || 0))
        : displayStr;

    return (
        <div ref={elRef} className="text-center" role="group" aria-label={label ? `${prefix || ''}${value || '0'}${suffix || ''} ${label}` : undefined}>
            <div className="font-bold" aria-hidden="true" style={{ color: valueColor || '#6366f1', fontSize: SIZES[valueSize] || '2.5rem', lineHeight: 1.1 }}>{prefix}{shown}{suffix}</div>
            {label && <p className="text-sm mt-2 font-medium" style={{ color: labelColor || '#6b7280' }}>{label}</p>}
        </div>
    );
}

// ── Gravity CSS lookup (hoisted out of render) ──
function CraftROICalculatorWidget({ sliders, formula, resultLabel, resultPrefix, accentColor }) {
    const parsedSliders = typeof sliders === 'string' ? (() => { try { return JSON.parse(sliders); } catch { return []; } })() : (sliders || []);
    const [vals, setVals] = useState(() => parsedSliders.map(s => s.default || s.min || 0));
    const c = accentColor || '#6366f1';
    const updateVal = (idx, v) => setVals(prev => { const n = [...prev]; n[idx] = Number(v); return n; });
    const result = formula === 'sum' ? vals.reduce((a, b) => a + b, 0) : vals.reduce((a, b) => a * b, 1) / 100;
    return (
        <div className="p-6 rounded-xl border" style={{ borderColor: '#e5e7eb' }}>
            <div className="space-y-5">
                {parsedSliders.map((s, i) => (
                    <div key={i}>
                        <div className="flex justify-between mb-1.5"><span className="text-sm font-medium" style={{ color: '#374151' }}>{s.label}</span><span className="text-sm font-bold" style={{ color: c }}>{vals[i] || 0}{s.suffix || ''}</span></div>
                        <input type="range" min={s.min || 0} max={s.max || 100} value={vals[i] || 0} onChange={e => updateVal(i, e.target.value)} className="w-full h-2 rounded-full appearance-none cursor-pointer" style={{ accentColor: c }}/>
                    </div>
                ))}
            </div>
            <div className="mt-6 p-4 rounded-lg text-center" style={{ backgroundColor: hexToRgba(c, 0.08) }}>
                <p className="text-sm font-medium mb-1" style={{ color: '#6b7280' }}>{resultLabel || 'Resultado'}</p>
                <p className="text-3xl font-extrabold" style={{ color: c }}>{resultPrefix || '$'}{Math.round(result).toLocaleString()}</p>
            </div>
        </div>
    );
}

function CraftFloatingButtonWidget({ icon, bgColor, link, size, pulse }) {
    const ICONS = {
        chat: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/></svg>,
        whatsapp: <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>,
        phone: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>,
        arrow: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18"/></svg>,
    };
    const sz = size === 'sm' ? '48px' : size === 'lg' ? '64px' : '56px';
    const bg = bgColor || '#25d366';
    return (
        <a {...resolveLinkProps(link || '#')} aria-label="Contactar" className="flex items-center justify-center rounded-full text-white shadow-lg"
            style={{ position: 'fixed', bottom: '24px', right: '24px', zIndex: 9999, width: sz, height: sz, backgroundColor: bg, animation: pulse && !PREFERS_REDUCED_MOTION ? 'cfloat-pulse 2s ease-in-out infinite' : 'none', '--fp-color': hexToRgba(bg, 0.4) }}>
            {ICONS[icon] || ICONS.chat}
        </a>
    );
}

function CraftPricingToggleWidget({ labelA, labelB, discount, priceA, priceB, accentColor }) {
    const [active, setActive] = useState(false);
    const c = accentColor || '#6366f1';
    return (
        <div className="flex flex-col items-center gap-4">
            <div className="flex items-center gap-3">
                <span className="text-sm font-medium" style={{ color: !active ? c : '#6b7280' }}>{labelA || 'Mensual'}</span>
                <button onClick={() => setActive(!active)} className="relative w-14 h-7 rounded-full transition-colors" style={{ backgroundColor: active ? c : '#d1d5db' }} role="switch" aria-checked={active} aria-label="Alternar precio">
                    <span className="absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform" style={{ left: active ? '30px' : '2px' }}/>
                </button>
                <span className="text-sm font-medium" style={{ color: active ? c : '#6b7280' }}>{labelB || 'Anual'}</span>
                {discount && <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: hexToRgba(c, 0.1), color: c }}>{discount}</span>}
            </div>
            <div className="text-center">
                <div className="text-4xl font-extrabold" style={{ color: '#1f2937' }}>{active ? (priceB || '$290') : (priceA || '$29')}</div>
                <p className="text-sm mt-1" style={{ color: '#6b7280' }}>{active ? (labelB || 'Anual') : (labelA || 'Mensual')}</p>
            </div>
        </div>
    );
}

function CraftScrollProgressWidget({ barColor, height, position }) {
    const [progress, setProgress] = useState(0);
    useEffect(() => {
        const onScroll = () => {
            const h = document.documentElement;
            const pct = (h.scrollTop / (h.scrollHeight - h.clientHeight)) * 100;
            setProgress(Math.min(100, Math.max(0, pct)));
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);
    return (
        <div style={{ position: 'fixed', [position === 'bottom' ? 'bottom' : 'top']: 0, left: 0, right: 0, zIndex: 9998, height: height || '4px', backgroundColor: 'transparent' }}>
            <div style={{ height: '100%', width: `${progress}%`, backgroundColor: barColor || '#6366f1', transition: 'width 0.1s linear' }}/>
        </div>
    );
}

function CraftBackToTopWidget({ bgColor, iconColor, size, borderRadius }) {
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        const onScroll = () => setVisible(window.scrollY > 400);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);
    if (!visible) return null;
    const sz = size === 'sm' ? '40px' : size === 'lg' ? '56px' : '48px';
    const br = RADII[borderRadius] || RADII.lg;
    return (
        <button onClick={() => window.scrollTo({ top: 0, behavior: PREFERS_REDUCED_MOTION ? 'auto' : 'smooth' })} aria-label="Volver arriba"
            className="flex items-center justify-center shadow-lg transition-opacity"
            style={{ position: 'fixed', bottom: '24px', left: '24px', zIndex: 9998, width: sz, height: sz, backgroundColor: bgColor || '#1f2937', color: iconColor || '#ffffff', borderRadius: br, border: 'none', cursor: 'pointer' }}>
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18"/></svg>
        </button>
    );
}

function CraftCodeBlockWidget({ code, language, showLineNumbers, bgColor, textColor }) {
    const [copied, setCopied] = useState(false);
    const lines = (code || '').split('\n');
    const handleCopy = () => {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(code || '').then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
        }
    };
    return (
        <div className="rounded-xl overflow-hidden" style={{ backgroundColor: bgColor || '#1e1e2e' }}>
            <div className="flex items-center justify-between px-4 py-2" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="flex gap-1.5"><span className="w-3 h-3 rounded-full bg-red-500/80"/><span className="w-3 h-3 rounded-full bg-yellow-500/80"/><span className="w-3 h-3 rounded-full bg-green-500/80"/></div>
                <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono" style={{ color: 'rgba(255,255,255,0.4)' }}>{language || 'javascript'}</span>
                    <button onClick={handleCopy} className="text-[10px] px-2 py-0.5 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', border: 'none', cursor: 'pointer' }}>{copied ? 'Copiado!' : 'Copiar'}</button>
                </div>
            </div>
            <pre className="p-4 overflow-x-auto text-sm font-mono leading-relaxed" style={{ color: textColor || '#cdd6f4', margin: 0 }}>
                {lines.map((line, i) => <div key={i} className="flex">{showLineNumbers !== false && <span className="select-none mr-4 text-right" style={{ color: 'rgba(255,255,255,0.2)', minWidth: '2ch' }}>{i + 1}</span>}<code>{line}</code></div>)}
            </pre>
        </div>
    );
}

function CraftBeforeAfterWidget({ beforeSrc, afterSrc, beforeLabel, afterLabel, borderRadius }) {
    const [pos, setPos] = useState(50);
    const [dragging, setDragging] = useState(false);
    const containerRef = useRef(null);
    const br = RADII[borderRadius] || RADII.xl;

    const handleMove = useCallback((clientX) => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
        setPos((x / rect.width) * 100);
    }, []);

    useEffect(() => {
        if (!dragging) return;
        const onMove = (e) => handleMove(e.touches ? e.touches[0].clientX : e.clientX);
        const onUp = () => setDragging(false);
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchmove', onMove, { passive: true });
        window.addEventListener('touchend', onUp);
        return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); window.removeEventListener('touchmove', onMove); window.removeEventListener('touchend', onUp); };
    }, [dragging, handleMove]);

    return (
        <div ref={containerRef} className="relative select-none overflow-hidden" style={{ borderRadius: br, aspectRatio: '4/3' }}
            onMouseDown={(e) => { setDragging(true); handleMove(e.clientX); }}
            onTouchStart={(e) => { setDragging(true); handleMove(e.touches[0].clientX); }}
            role="img" aria-label={`Comparacion: ${beforeLabel || 'Antes'} vs ${afterLabel || 'Despues'}`}>
            {afterSrc ? <img src={afterSrc} alt={afterLabel || 'Despues'} className="absolute inset-0 w-full h-full object-cover" loading="lazy"/> : <div className="absolute inset-0 bg-gray-200 flex items-center justify-center"><span className="text-sm text-gray-400">{afterLabel || 'Despues'}</span></div>}
            <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
                {beforeSrc ? <img src={beforeSrc} alt={beforeLabel || 'Antes'} className="absolute inset-0 w-full h-full object-cover" style={{ minWidth: containerRef.current ? `${containerRef.current.offsetWidth}px` : '100%' }} loading="lazy"/> : <div className="absolute inset-0 bg-gray-300 flex items-center justify-center"><span className="text-sm text-gray-500">{beforeLabel || 'Antes'}</span></div>}
            </div>
            <div className="absolute top-0 bottom-0" style={{ left: `${pos}%`, transform: 'translateX(-50%)' }}>
                <div className="w-0.5 h-full bg-white shadow-lg"/>
                <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center cursor-ew-resize">
                    <svg viewBox="0 0 24 24" className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 6l-4 6 4 6M16 6l4 6-4 6"/></svg>
                </div>
            </div>
            <span className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">{beforeLabel || 'Antes'}</span>
            <span className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded">{afterLabel || 'Despues'}</span>
        </div>
    );
}

const GRAV_CSS = {
    'top-left':{as:'flex-start',js:'flex-start'},'top-center':{as:'flex-start',js:'center'},'top-right':{as:'flex-start',js:'flex-end'},
    'center-left':{as:'center',js:'flex-start'},'center':{as:'center',js:'center'},'center-right':{as:'center',js:'flex-end'},
    'bottom-left':{as:'flex-end',js:'flex-start'},'bottom-center':{as:'flex-end',js:'center'},'bottom-right':{as:'flex-end',js:'flex-end'},
};

const CraftTreeRenderer = memo(function CraftTreeRenderer({ craftState, visible, block }) {
    const nodes = useMemo(() => {
        if (!craftState) return null;
        try { return typeof craftState === 'string' ? JSON.parse(craftState) : craftState; }
        catch { return null; }
    }, [craftState]);

    if (!nodes) return null;

    const formCtx = block ? { blockType: block.type, blockId: block.id } : {};

    const renderNode = (nodeId, idx, parentProps, depth = 0) => {
        if (depth > MAX_RENDER_DEPTH) return null;
        const node = nodes[nodeId];
        if (!node) return null;
        const typeName = node.type?.resolvedName;
        const props = node.props || {};
        const childIds = node.nodes || [];
        const renderer = CRAFT_RENDERERS[typeName];
        if (!renderer) return null;

        const isParentFree = props.layoutMode === 'free';
        const isParentStack = props.layoutMode === 'stack';
        const children = childIds.length > 0 ? childIds.map((id, i) => {
            const childNode = nodes[id];
            const childCustom = childNode?.custom || {};
            let childStyle = {};
            if (isParentFree) {
                childStyle = { position: 'absolute', left: `${parseFloat(childCustom.freeX) || 0}px`, top: `${parseFloat(childCustom.freeY) || 0}px` };
            } else if (isParentStack) {
                const g = GRAV_CSS[childCustom.gravity || props.childGravity || 'top-left'] || GRAV_CSS['top-left'];
                childStyle = { gridArea: '1 / 1', alignSelf: g.as, justifySelf: g.js };
                if (childCustom.stackOffsetX || childCustom.stackOffsetY) {
                    childStyle.margin = `${parseFloat(childCustom.stackOffsetY) || 0}% 0 0 ${parseFloat(childCustom.stackOffsetX) || 0}%`;
                }
            }
            return <div key={id} style={childStyle}>{renderNode(id, i, props, depth + 1)}</div>;
        }) : null;

        return (
            <div key={nodeId} className={`transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                style={{ transitionDelay: visible ? `${100 + idx * 60}ms` : '0ms' }}>
                {renderer(props, children, formCtx)}
            </div>
        );
    };

    const root = nodes['ROOT'];
    if (!root?.nodes?.length) return null;
    return <>{root.nodes.map((id, i) => renderNode(id, i, null, 0))}</>;
});

// ── Legacy Grid Renderer ──

function GridRenderer({content, widgets, sharedWidgetProps, visible}) {
    let cells = content.cells;
    if (!cells && widgets.length > 0) {
        cells = widgets.filter(w => w.type).map(w => ({
            id: w.id, row: w.row, col: w.col, rowSpan: w.rowSpan, colSpan: w.colSpan,
            widgets: [{id: w.id, type: w.type, data: w.data, style: w.style}],
        }));
    }
    const validCells = (cells || []).filter(c => c.widgets?.length > 0);
    let globalWidgetIdx = 0;
    return (
        <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${content.grid.cols || 4}, 1fr)`,
            gridTemplateRows: `repeat(${content.grid.rows || 2}, minmax(60px, auto))`,
            gap: ({sm: '8px', md: '12px', lg: '20px'})[content.gap] || '12px',
        }}>
            {validCells.map((cell, cIdx) => (
                <div key={cell.id || cIdx}
                    className={`flex flex-col gap-3 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                    style={{
                        gridColumn: `${cell.col || 1} / span ${cell.colSpan || 1}`,
                        gridRow: `${cell.row || 1} / span ${cell.rowSpan || 1}`,
                        transitionDelay: visible ? `${150 + cIdx * 80}ms` : '0ms',
                    }}>
                    {cell.widgets.map((widget, wIdx) => {
                        const idx = globalWidgetIdx++;
                        return <WidgetRenderer key={widget.id || `${cIdx}-${wIdx}`} widget={widget} widgetIndex={idx} {...sharedWidgetProps}/>;
                    })}
                </div>
            ))}
        </div>
    );
}

// ── Main Section ──

export default function CustomSection({ block = {}, primaryColor, secondaryColor, enableAnimations, mediaFolders = {}, cardStyle }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const layout = content.layout || {};
    const widgets = content.widgets || [];
    const columns = layout.columns || [];
    const reverseMobile = layout.reverse_mobile || false;
    const gapCls = GAP[layout.gap] || GAP.md;

    const widgetsByColumn = useMemo(() => {
        const map = {};
        widgets.forEach(w => {
            const colId = w.column_id || 'default';
            if (!map[colId]) map[colId] = [];
            map[colId].push(w);
        });
        Object.values(map).forEach(arr => arr.sort((a, b) => (a.order || 0) - (b.order || 0)));
        return map;
    }, [widgets]);

    const cells = content.cells || [];
    const hasCraftState = !!content.craftState;

    if (!hasCraftState && widgets.length === 0 && columns.length === 0 && cells.length === 0) return null;

    const sectionCls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };
    const sharedWidgetProps = { s, primaryColor, secondaryColor, enableAnimations, mediaFolders, visible };

    return (
        <section className={sectionCls} style={bgS}>
            <CraftPubStyles />
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <SectionBgImage config={config} mediaFolders={mediaFolders} />
            <div ref={ref} className={`relative z-10 ${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />

                {hasCraftState ? (
                    /* New Craft.js format */
                    <CraftErrorBoundary>
                        <CraftTreeRenderer craftState={content.craftState} visible={visible} block={block} />
                    </CraftErrorBoundary>
                ) : content.grid ? (
                    <GridRenderer content={content} widgets={widgets} sharedWidgetProps={sharedWidgetProps} visible={visible}/>
                ) : columns.length > 0 ? (
                    <div className={`grid grid-cols-12 ${gapCls} ${reverseMobile ? 'flex flex-col-reverse md:grid' : ''}`}>
                        {columns.map((col, colIdx) => {
                            const span = col.span || 12;
                            const mdSpan = col.md_span || span;
                            const colWidgets = widgetsByColumn[col.id] || [];
                            const colCls = `${COL_SPAN[12]} ${MD_COL_SPAN[mdSpan] || MD_COL_SPAN[span]}`;
                            return (
                                <div key={col.id || colIdx} className={`${colCls} flex flex-col gap-4`}>
                                    {colWidgets.map((widget, wIdx) => (
                                        <div key={widget.id || wIdx}
                                            className={`transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                                            style={{transitionDelay: visible ? `${150 + (colIdx * 3 + wIdx) * 80}ms` : '0ms'}}>
                                            <WidgetRenderer widget={widget} widgetIndex={colIdx * 3 + wIdx} {...sharedWidgetProps} />
                                        </div>
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="flex flex-col gap-4">
                        {widgets.filter(w => w.type).sort((a, b) => (a.order || 0) - (b.order || 0)).map((widget, wIdx) => (
                            <div key={widget.id || wIdx}
                                className={`transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                                style={{transitionDelay: visible ? `${150 + wIdx * 80}ms` : '0ms'}}>
                                <WidgetRenderer widget={widget} {...sharedWidgetProps} />
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
